import {
    Inject,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    signal,
    WritableSignal,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import {
    ArrayModel,
    ConfigConstants,
    ConfirmationInstallationPaperworkModel,
    DataMaintenanceSystemConfigModel,
    DisplayDecorator,
    Fda2579FormModel,
    isDefined,
    ModalClass,
    NotificationModel,
    ODataModel,
    ODataModelChangeEvent,
    TextModel,
    TextModelOptions,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { faCheckCircle, faTimesCircle, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { ConfirmationFDA2579FormComponent } from '../confirmation-FDA2579-form/confirmation-fda2579-form.component';
import { filter, take, tap } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormList,
    FormListEntryState,
    FormsConstants,
    FormsState,
    getListForNotificationFactory,
    initialDocumentState,
    UploadModalComponent,
} from '@evorait/pace-forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DataMaintenanceSysconfigComponent } from '../../details/data-maintenance/data-maintenance-sysconfig.component';

type UploadType = 'DAY_ONE' | 'FINAL_DAY' | 'PRE_RAMP' | 'IN15_SUR' | 'IN16_IQAP' | 'IN26_SRT' | 'IN30_SUR' | 'IN31_IR';
const HELIUM_TRACKER_LINK = 'https://helium-tracking.siemens-healthineers.cn/systems/search';
const SIEMENS_CLOUD_DIRECTORY_LINK = 'https://scd.siemens.cloud/en/search';

@Component({
    selector: 'pace-confirmation-installation-paperwork',
    templateUrl: 'confirmation-installation-paperwork.component.html',
    styleUrl: 'confirmation-installation-paperwork.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false,
})
export class ConfirmationInstallationPaperworkComponent implements OnInit {
    @Input()
    public model: ConfirmationInstallationPaperworkModel;

    @Input()
    public notification: NotificationModel;

    @Input()
    public parentForm: FormArray;

    @Output()
    public openPaceForms = new EventEmitter<void>();

    @Output()
    public updateFollowUp = new EventEmitter<ConfigConstants.INSTALLATION_PAPERWORK_FOLLOWUP_SHORT_TEXT>();

    protected installationPaperworkForm: FormGroup;
    protected installationPaperworkFormChildren: FormArray;
    protected oemComponentForm: FormArray;
    protected readonly newSoldEquipmentModel = {
        oemType: new TextModel('OEM_Type', '', new TextModelOptions({ minLength: 1, placeholder: 'Type' })),
        oemModelNr: new TextModel('OEM_ModelNr', '', new TextModelOptions({ minLength: 1, placeholder: 'Model Nr.' })),
        oemSerialNr: new TextModel(
            'OEM_SerialNr',
            '',
            new TextModelOptions({ minLength: 1, placeholder: 'Serial Nr.' }),
        ),
    };
    protected iseCseMetricFormAppLink = '';

    protected readonly isFirstDay: WritableSignal<boolean> = signal(false);
    protected readonly firstDayFileUploads: WritableSignal<string[]> = signal([]);
    protected readonly firstDayValid = computed(
        () =>
            this.model.dayOneAttachments.options.completed() ||
            this.firstDayFileUploads().length > 0 ||
            !this.model.dayOneAttachments.options.required(),
    );
    protected readonly finalDayFileUploads: WritableSignal<string[]> = signal([]);
    protected readonly finalDayValid = computed(
        () =>
            this.model.finalDayAttachments.options.completed() ||
            this.finalDayFileUploads().length > 0 ||
            !this.model.finalDayAttachments.options.required(),
    );
    protected readonly preRampAttachments: WritableSignal<string[]> = signal([]);
    protected readonly preRampValid = computed(
        () =>
            this.model.preRampPhotos.options.completed() ||
            this.preRampAttachments().length > 0 ||
            !this.model.preRampPhotos.options.required(),
    );
    protected readonly isChillerInfoNeeded: WritableSignal<boolean> = signal(false);
    protected readonly isStartupStatusSelected: WritableSignal<boolean> = signal(false);
    protected readonly showPunchLists = signal(false);
    protected readonly isLeadResponsibleSelectionTrue: WritableSignal<boolean> = signal(false);
    protected readonly isSerialNumberDifferentFromIVKSelected: WritableSignal<number> = signal(-1);
    protected readonly isSyngoClassic: WritableSignal<boolean> = signal(false);
    protected readonly isFdaFormValid: WritableSignal<boolean> = signal(false);
    protected readonly isFdaFormRequired: WritableSignal<boolean> = signal(true);

    protected readonly isPaceStartupAttachedValid: WritableSignal<boolean> = signal(false);
    protected readonly installationReportValid: WritableSignal<boolean> = signal(false);
    protected readonly syngoSafetyRelatedReportValid: WritableSignal<boolean> = signal(false);
    protected readonly startupReportMRValid: WritableSignal<boolean> = signal(false);
    protected readonly iqapReportValid: WritableSignal<boolean> = signal(false);

    protected readonly isSiemensPunchListAddDisabled = signal(false);
    protected readonly isCustomerPunchListAddDisabled = signal(false);
    protected readonly showSiteTypeComment = signal(false);

    protected readonly faTrashAlt = faTrashAlt;
    protected readonly FormStatusFinal = FormsConstants.SUBMIT_STATUS.FINAL;
    protected readonly heliumTrackerLink = HELIUM_TRACKER_LINK;
    protected readonly siemensCloudDirectoryLink = SIEMENS_CLOUD_DIRECTORY_LINK;
    protected readonly faTimesCircle = faTimesCircle;
    protected readonly faCheckCircle = faCheckCircle;

    public constructor(
        private readonly formBuilder: FormBuilder,
        private readonly modalService: BsModalService,
        private readonly cd: ChangeDetectorRef,
        private readonly translate: TranslateService,
        private readonly store: Store<FormsState>,
        private readonly destroyRef$: DestroyRef,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {}

    public ngOnInit(): void {
        this.iseCseMetricFormAppLink = this.configService.getIseCseMetricFormPowerApp();
        const installationPaperworkValidator = (): ValidationErrors | null => this.checkValidity();
        this.installationPaperworkFormChildren = this.formBuilder.array([]);
        this.installationPaperworkForm = this.formBuilder.group(
            {
                children: this.installationPaperworkFormChildren,
            },
            {
                validators: installationPaperworkValidator,
            },
        );
        this.parentForm.push(this.installationPaperworkForm);
        this.oemComponentForm = this.formBuilder.array([], {
            validators: (): ValidationErrors | null => this.checkOemValidity(),
        });

        this.store
            .select(getListForNotificationFactory({ notification: this.notification.identifier }))
            .pipe(
                takeUntilDestroyed(this.destroyRef$),
                filter((formsList): formsList is FormList => !!formsList),
                tap((formsList) => {
                    this.updateFormsListStatus(formsList);
                }),
            )
            .subscribe();

        this.isStartupStatusSelected.set(!!this.model.startupStatus.selectedEntry?.value);
        this.showPunchLists.set(
            this.model.startupStatus.selectedEntry?.value ===
                ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists,
        );
        this.isFirstDay.set(this.model.isSystemReadyForStartupAndDayOne.getSelectedIndex() === 0);
        this.firstDayFileUploads.set([...this.model.dayOneAttachments.fileNames]);
        this.finalDayFileUploads.set([...this.model.finalDayAttachments.fileNames]);
        this.preRampAttachments.set([...this.model.preRampPhotos.fileNames]);
        this.isChillerInfoNeeded.set(this.model.isChillerFlModelSerialGiven.getSelectedIndex() === 0);
        this.isLeadResponsibleSelectionTrue.set(
            this.model.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes,
        );
        this.isSiemensPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.siemensPunchlist));
        this.isCustomerPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.customerPunchlist));
        this.showSiteTypeComment.set(
            this.model.siteType.selectedEntry?.value === ConfigConstants.INSTALLATION_PAPERWORK_SITE_TYPE.existing,
        );
        this.setSerialNumberDifferentThanIVK();
        this.setIsSyngoClassic();

        this.isFdaFormValid.set(
            this.model.attachFDA2579FormCompleted() || this.model.attachFDA2579Form.isFda2579FormValid,
        );
        this.updateModelOptions();
        this.updateFollowUp.emit(this.model.generateFollowUpShortText());
    }

    protected addSiemensPunchcard(): void {
        this.model.addPunchlistItem(this.model.siemensPunchlist);
        this.isSiemensPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.siemensPunchlist));
    }

    protected addCustomerPunchcard(): void {
        this.model.addPunchlistItem(this.model.customerPunchlist);
        this.isCustomerPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.customerPunchlist));
    }

    protected removeSiemensPunchcard(index: number): void {
        this.model.siemensPunchlist.removeModel(index);
        this.isSiemensPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.siemensPunchlist));
    }

    protected removeCustomerPunchcard(index: number): void {
        this.model.customerPunchlist.removeModel(index);
        this.isCustomerPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.customerPunchlist));
    }

    protected updatePunchlistStatus(): void {
        this.isSiemensPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.siemensPunchlist));
        this.isCustomerPunchListAddDisabled.set(this.disablePunchlistAddButton(this.model.customerPunchlist));
    }

    private disablePunchlistAddButton(model: ArrayModel<TextModel>): boolean {
        const punchlistLength = model.value?.length;
        return (
            (punchlistLength > 0 &&
                model.value.some(
                    (entry) =>
                        entry.value === '' ||
                        entry.value.length < entry.options.minLength ||
                        entry.value.length > entry.options.maxLength,
                )) ||
            punchlistLength >= model.options.maxEntries
        );
    }

    private updateModelOptions(): void {
        this.model.updateModelOptions();
        this.isFdaFormRequired.set(this.model.attachFDA2579Form.options.required);
        this.checkFormsValidity();
    }

    protected updateFirstDayDependencies(event: ODataModelChangeEvent<DisplayDecorator<ODataModel>>): void {
        if (event.newValue === ConfigConstants.optionYes || event.oldValue === ConfigConstants.optionYes) {
            this.isFirstDay.set(event.newValue === ConfigConstants.optionYes);
            this.updateModelOptions();
        }
    }

    protected updateIsChillerInfoNeeded(event: ODataModelChangeEvent<DisplayDecorator<ODataModel>>): void {
        this.isChillerInfoNeeded.set(event.newValue === ConfigConstants.optionYes);
        this.updateModelOptions();
    }

    protected updateStartupStatus(event: ODataModelChangeEvent<DisplayDecorator<ODataModel>>): void {
        this.isStartupStatusSelected.set(!!event.newValue);
        this.showPunchLists.set(
            this.model.startupStatus.selectedEntry?.value ===
                ConfigConstants.INSTALLATION_PAPERWORK_SYSTEM_STARTUP_STATUS.startupCompleteOpenPunchlists,
        );
        this.updateModelOptions();
        this.cd.detectChanges();
        this.updateFollowUp.emit(this.model.generateFollowUpShortText());
    }

    protected updateLeadResponsibleRole(): void {
        this.isLeadResponsibleSelectionTrue.set(
            this.model.isPersonLeadResponsible.selectedEntry.value === ConfigConstants.optionYes,
        );
        this.updateModelOptions();
        this.cd.detectChanges();
        this.updateFollowUp.emit(this.model.generateFollowUpShortText());
    }

    protected setSerialNumberDifferentThanIVK(): void {
        this.isSerialNumberDifferentFromIVKSelected.set(this.model.isSerialNumberDifferentThanIVK.getSelectedIndex());
    }

    protected setIsSyngoClassic(): void {
        this.isSyngoClassic.set(
            this.model.syngoClassicOrNativeSoftware.selectedEntry?.value ===
                ConfigConstants.INSTALLATION_PAPERWORK_SYNGO_SOFTWARE.classic,
        );
        this.updateModelOptions();
    }

    protected deleteSoldEquipment(index: number): void {
        this.model.soldOEMEquipmentList.splice(index, 1);
    }

    protected addSoldEquipment(): void {
        if (
            this.newSoldEquipmentModel.oemType.value.length > 0 &&
            this.newSoldEquipmentModel.oemModelNr.value.length > 0 &&
            this.newSoldEquipmentModel.oemSerialNr.value.length > 0
        ) {
            this.model.soldOEMEquipmentList.push(
                `${this.newSoldEquipmentModel.oemType.value} / ${this.newSoldEquipmentModel.oemModelNr.value} / ${this.newSoldEquipmentModel.oemSerialNr.value}`,
            );
            this.newSoldEquipmentModel.oemType.value = '';
            this.newSoldEquipmentModel.oemModelNr.value = '';
            this.newSoldEquipmentModel.oemSerialNr.value = '';
            this.oemComponentForm.controls.forEach((control) => {
                control.patchValue('');
            });
        }
    }

    protected openFDA2579(): void {
        const modalInstance = this.modalService.show(ConfirmationFDA2579FormComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_FULLSCREEN,
            initialState: {
                model: this.model.attachFDA2579Form,
                notification: this.notification,
            },
            ariaLabelledBy: 'internal-info-modal-title',
        });
        modalInstance.content?.modalResult
            .pipe(
                tap((savedFDAFormModel: Fda2579FormModel) => {
                    if (savedFDAFormModel) {
                        this.model.attachFDA2579Form = savedFDAFormModel;
                        this.isFdaFormValid.set(this.model.attachFDA2579Form.isFda2579FormValid);
                        this.installationPaperworkFormChildren.controls.forEach((control) =>
                            control.updateValueAndValidity(),
                        );
                        this.cd.detectChanges();
                    }
                }),
                takeUntilDestroyed(this.destroyRef$),
                take(1),
            )
            .subscribe();
    }

    protected uploadFile(uploadType: UploadType): void {
        let sapID = '';
        let filePrefix = '';
        switch (uploadType) {
            case 'DAY_ONE':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.isSystemReadyForStartupAndDayOne;
                filePrefix = `${sapID}_Day1`;
                break;
            case 'FINAL_DAY':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.finalDayAttachments;
                filePrefix = `${sapID}_FinalDay`;
                break;
            case 'PRE_RAMP':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.preRampPhotos;
                filePrefix = `${sapID}_Preramp`;
                break;
            case 'IN15_SUR':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.isPaceStartupAttached;
                filePrefix = uploadType;
                break;
            case 'IN16_IQAP':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachIQAPReport;
                filePrefix = uploadType;
                break;
            case 'IN26_SRT':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.attachSyngoSafetyRelatedReport;
                filePrefix = uploadType;
                break;
            case 'IN30_SUR':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.startupReportMR;
                filePrefix = uploadType;
                break;
            case 'IN31_IR':
                sapID = ConfigConstants.INSTALLATION_PAPERWORK_CODES.installationReport;
                filePrefix = uploadType;
                break;
        }

        const uploadToDocBoxInitialState = {
            docBoxModel: {
                ...initialDocumentState,
                orderNumber: this.notification.identifier.qmNum,
                isFileVisible: false,
                sapID,
                paperworkUpload: true,
                taskNumber: this.notification.identifier.maNum,
            },
            notification: this.notification.getNotificationState(),
            filePrefix,
        };

        const modalInstance = this.modalService.show(UploadModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: uploadToDocBoxInitialState,
            ariaLabelledBy: 'upload-modal-title',
        });

        const SUCCESSFUL_UPLOAD_PREFIX = '1_YES';

        modalInstance.content?.modalResult
            ?.pipe(takeUntilDestroyed(this.destroyRef$), take(1))
            .subscribe((document) => {
                if (isDefined(document)) {
                    const reportStatus = document.reportStatus ?? FormsConstants.SUBMIT_STATUS.UNKNOWN;
                    const documentTitle = `${SUCCESSFUL_UPLOAD_PREFIX}:${reportStatus}:${document.title}`;
                    switch (uploadType) {
                        case 'DAY_ONE':
                            this.model.dayOneAttachments.fileNames.push(document.title);
                            this.firstDayFileUploads.set([...this.model.dayOneAttachments.fileNames]);
                            break;
                        case 'FINAL_DAY':
                            this.model.finalDayAttachments.fileNames.push(document.title);
                            this.finalDayFileUploads.set([...this.model.finalDayAttachments.fileNames]);
                            break;
                        case 'PRE_RAMP':
                            this.model.preRampPhotos.fileNames.push(document.title);
                            this.preRampAttachments.set([...this.model.preRampPhotos.fileNames]);
                            break;
                        case 'IN15_SUR':
                            // To update the value of this readonly text model in the template, we need to update the reference.
                            // Because the TextModel is a complex object with functions like `getOutModel()` a simple reassignment
                            // is not possible, but instead requires a deep clone of the object.
                            this.model.isPaceStartupAttached.value = documentTitle;
                            this.model.isPaceStartupAttached = structuredClone(this.model.isPaceStartupAttached);
                            this.model.startupFormSubmitStatus.set(reportStatus);
                            this.isPaceStartupAttachedValid.set(true);
                            break;
                        case 'IN16_IQAP':
                            // To update the value of this readonly text model in the template, we need to update the reference.
                            // Because the TextModel is a complex object with functions like `getOutModel()` a simple reassignment
                            // is not possible, but instead requires a deep clone of the object.
                            this.model.attachIQAPReport.value = documentTitle;
                            this.model.attachIQAPReport = structuredClone(this.model.attachIQAPReport);
                            this.model.iqapReportSubmitStatus.set(reportStatus);
                            this.iqapReportValid.set(true);
                            break;
                        case 'IN26_SRT':
                            // To update the value of this readonly text model in the template, we need to update the reference.
                            // Because the TextModel is a complex object with functions like `getOutModel()` a simple reassignment
                            // is not possible, but instead requires a deep clone of the object.
                            this.model.attachSyngoSafetyRelatedReport.value = documentTitle;
                            this.model.attachSyngoSafetyRelatedReport = structuredClone(
                                this.model.attachSyngoSafetyRelatedReport,
                            );
                            this.model.syngoSafetyReportSubmitStatus.set(reportStatus);
                            this.syngoSafetyRelatedReportValid.set(true);
                            break;
                        case 'IN30_SUR':
                            // To update the value of this readonly text model in the template, we need to update the reference.
                            // Because the TextModel is a complex object with functions like `getOutModel()` a simple reassignment
                            // is not possible, but instead requires a deep clone of the object.
                            this.model.startupReportMR.value = documentTitle;
                            this.model.startupReportMR = structuredClone(this.model.startupReportMR);
                            this.model.startupReportMRSubmitStatus.set(reportStatus);
                            this.startupReportMRValid.set(true);
                            break;
                        case 'IN31_IR':
                            // To update the value of this readonly text model in the template, we need to update the reference.
                            // Because the TextModel is a complex object with functions like `getOutModel()` a simple reassignment
                            // is not possible, but instead requires a deep clone of the object.
                            this.model.installationReport.value = documentTitle;
                            this.model.installationReport = structuredClone(this.model.installationReport);
                            this.model.installationReportSubmitStatus.set(reportStatus);
                            this.installationReportValid.set(true);
                            break;
                        default:
                            break;
                    }
                    this.installationPaperworkForm.updateValueAndValidity();
                    this.cd.markForCheck();
                    this.updateFollowUp.emit(this.model.generateFollowUpShortText());
                }
            });
    }

    protected checkPaceForms(): void {
        this.openPaceForms.emit();
    }
    protected updateSiteType(): void {
        this.showSiteTypeComment.set(
            this.model.siteType.selectedEntry?.value === ConfigConstants.INSTALLATION_PAPERWORK_SITE_TYPE.existing,
        );
    }
    public openEquipmentConfig(): void {
        this.modalService.show(DataMaintenanceSysconfigComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                model: new DataMaintenanceSystemConfigModel(this.notification),
            },
            ariaLabelledBy: 'data-maintenance-system-config-modal-title',
        });
    }

    private updateFormsListStatus(formsList: FormList): void {
        if (this.model.startupFormSubmitStatus() !== FormsConstants.SUBMIT_STATUS.FINAL) {
            const form = this.getCorrectlySubmittedFormId(formsList.entries, [
                FormsConstants.FORMTYPE.STARTUP_REPORT,
                FormsConstants.FORMTYPE.INSTALLATION_STARTUP_REPORT,
            ]);
            if (isDefined(form)) {
                this.model.isPaceStartupAttached.value = form.id.formId + ':' + form.submitStatus;
                this.model.startupFormSubmitStatus.set(form.submitStatus);
                this.model.isPaceStartupAttached = structuredClone(this.model.isPaceStartupAttached);
            }
        }
        if (this.model.installationReportSubmitStatus() !== FormsConstants.SUBMIT_STATUS.FINAL) {
            const form = this.getCorrectlySubmittedFormId(formsList.entries, [
                FormsConstants.FORMTYPE.INSTALLATION_REPORT,
                FormsConstants.FORMTYPE.INSTALLATION_STARTUP_REPORT,
                FormsConstants.FORMTYPE.UPGRADE_REPORT,
            ]);
            if (isDefined(form)) {
                this.model.installationReport.value = form.id.formId + ':' + form.submitStatus;
                this.model.installationReportSubmitStatus.set(form.submitStatus);
                this.model.installationReport = structuredClone(this.model.installationReport);
            }
        }
        this.checkFormsValidity();
        this.cd.detectChanges();
        this.updateFollowUp.emit(this.model.generateFollowUpShortText());
    }

    private getCorrectlySubmittedFormId(
        formEntries: FormListEntryState[],
        acceptableFormTypes: FormsConstants.FORMTYPE[],
    ): FormListEntryState | undefined {
        const acceptableSubmitStatus = [
            FormsConstants.SUBMIT_STATUS.FINAL,
            FormsConstants.SUBMIT_STATUS.INTERIM,
            FormsConstants.SUBMIT_STATUS.INTERIM_NOT_OK,
            FormsConstants.SUBMIT_STATUS.FINAL_NOT_OK,
        ];
        return formEntries.find(
            (form) => acceptableFormTypes.includes(form.type) && acceptableSubmitStatus.includes(form.submitStatus),
        );
    }

    private checkFormsValidity(): void {
        this.isPaceStartupAttachedValid.set(
            this.model.isPaceStartupAttached.value !== '' || !this.model.isPaceStartupAttached.options?.required,
        );
        this.iqapReportValid.set(
            this.model.attachIQAPReport.value !== '' || !this.model.attachIQAPReport.options?.required,
        );
        this.syngoSafetyRelatedReportValid.set(
            this.model.attachSyngoSafetyRelatedReport.value !== '' ||
                !this.model.attachSyngoSafetyRelatedReport.options?.required,
        );
        this.startupReportMRValid.set(
            this.model.startupReportMR.value !== '' || !this.model.startupReportMR.options?.required,
        );
        this.installationReportValid.set(
            this.model.installationReport.value !== '' || !this.model.installationReport.options?.required,
        );
    }

    private checkValidity(): ValidationErrors | null {
        const errors: ValidationErrors = {};
        if (this.model.attachFDA2579Form.options.required && !this.isFdaFormValid()) {
            errors.fdaFormMissing = { text: 'FDA 2579 Form invalid or missing' };
        }
        if (!this.firstDayValid()) {
            errors.firstDayAttachmentsMissing = { text: 'The first day images are missing.' };
        }
        if (!this.finalDayValid()) {
            errors.finalDayAttachmentsMissing = { text: 'The final day images are missing.' };
        }
        if (!this.preRampValid()) {
            errors.preRampAttachmentsMissing = { text: 'The pre-ramp images are missing.' };
        }
        if (!this.isPaceStartupAttachedValid()) {
            errors.startupReportMissing = { text: 'The Startup Report is missing' };
        }
        if (!this.iqapReportValid()) {
            errors.iqapReportMissing = { text: 'The IQAP Report is missing' };
        }
        if (!this.syngoSafetyRelatedReportValid()) {
            errors.syngoSafetyReportMissing = { text: 'The Safety Related Report is missing' };
        }
        if (!this.startupReportMRValid()) {
            errors.startupReportMRMissing = { text: 'The Startup Report for MR is missing' };
        }
        if (!this.installationReportValid()) {
            errors.installationReportMissing = { text: 'The Installation Report is missing' };
        }
        return Object.keys(errors).length > 0 ? errors : null;
    }

    private checkOemValidity(): ValidationErrors | null {
        if (this.oemComponentForm?.controls?.some((control) => control.value === '')) {
            return { emptyValues: { text: 'Empty values are not allowed' } };
        }
        return null;
    }
}
